import React, { useMemo } from "react";
import { RefreshCw } from "lucide-react";
import { API, Card, FeedBody, useFeed } from "./ui";
import { useWidth } from "./charts";
import { PortfolioRisk } from "./LiveDesk";
import { count, money, pct, tone, tradePnl } from "./format";

const ROLE = { LAB: "Research lab", PERSONAL: "Personal", REAL_LIFE: "Real life", RESEARCH: "Research" };

function AccountCards({ accounts, trades, filter, onFilter, onDetail }) {
  const byAcct = useMemo(() => {
    const m = {};
    trades.forEach((t) => {
      const a = t.account_id || t.account;
      if (!m[a]) m[a] = { pnl: 0, wins: 0, graded: 0, n: 0 };
      const p = tradePnl(t);
      m[a].pnl += p; m[a].n += 1;
      if (p !== 0) m[a].graded += 1;
      if (p > 0) m[a].wins += 1;
    });
    return m;
  }, [trades]);
  return (
    <div className="mk-acct-row">
      {accounts.map((a) => {
        const s = byAcct[a.id] || { pnl: 0, wins: 0, graded: 0, n: 0 };
        const on = filter === a.id;
        return (
          <article key={a.id} className={`mk-card mk-acct ${on ? "is-on" : ""}`}>
            <header className="mk-acct-head">
              <div><h3>{a.id}</h3><p className="mk-scope">{ROLE[a.id] || "Account"}</p></div>
              <strong className="mk-num mk-big">{money(a.balance, { sign: false })}</strong>
            </header>
            <dl className="mk-kv is-grid">
              <div><dt>Realized P&amp;L</dt><dd className={`mk-num ${tone(s.pnl)}`}>{money(s.pnl)}</dd></div>
              <div><dt>Win rate</dt><dd className="mk-num">{s.graded ? pct((s.wins / s.graded) * 100) : "—"}</dd></div>
              <div><dt>Open positions</dt><dd className="mk-num">{count(a.open_count)}</dd></div>
              <div><dt>Open P&amp;L</dt><dd className={`mk-num ${tone(a.unrealized_pnl)}`}>{money(a.unrealized_pnl)}</dd></div>
            </dl>
            <p className="mk-scope">Realized = sum of {count(s.n)} closed trades, all time. Balance is the account ledger.</p>
            <div className="mk-acct-actions">
              <button type="button" className={`mk-btn ${on ? "is-lime" : ""}`} aria-pressed={on} onClick={() => onFilter(on ? "ALL" : a.id)}>
                {on ? "Filtering workspace" : "Filter workspace"}
              </button>
              <button type="button" className="mk-btn" onClick={() => onDetail(a)}>View trades</button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function FanChart({ bands }) {
  const [ref, W] = useWidth();
  const H = 300;
  const pad = { t: 10, r: 12, b: 24, l: 60 };
  const vals = bands.flatMap((b) => [b.p5, b.p95]).concat(0);
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const span = hi - lo || 1;
  const X = (i) => pad.l + (i / (bands.length - 1)) * (W - pad.l - pad.r);
  const Y = (v) => pad.t + (1 - (v - lo) / span) * (H - pad.t - pad.b);
  const line = (k) => bands.map((b, i) => `${i ? "L" : "M"}${X(i).toFixed(1)},${Y(b[k]).toFixed(1)}`).join(" ");
  const area = (a, b) => `${line(a)} ${[...bands].reverse().map((x, i) => `L${X(bands.length - 1 - i).toFixed(1)},${Y(x[b]).toFixed(1)}`).join(" ")} Z`;
  return (
    <div ref={ref} className="mk-chart">
      <svg width={W} height={H} role="img" aria-label="Monte Carlo equity fan">
        {[hi, 0, lo].map((v, i) => (
          <g key={i}>
            <line x1={pad.l} x2={W - pad.r} y1={Y(v)} y2={Y(v)} className={v === 0 ? "mk-zero-line" : "mk-grid-line"} />
            <text x={pad.l - 6} y={Y(v) + 4} textAnchor="end" className="mk-axis">{money(v, { dec: 0 })}</text>
          </g>
        ))}
        <path d={area("p5", "p95")} className="mk-fan-outer" />
        <path d={area("p25", "p75")} className="mk-fan-inner" />
        <path d={line("p50")} fill="none" className="mk-fan-median" />
        {[0, Math.floor(bands.length / 2), bands.length - 1].map((i, k) => (
          <text key={k} x={X(i)} y={H - 6} textAnchor={k === 0 ? "start" : k === 2 ? "end" : "middle"} className="mk-axis">trade {bands[i]?.trade}</text>
        ))}
      </svg>
    </div>
  );
}

function MonteCarlo() {
  const feed = useFeed(`${API}/backtest/v21/monte-carlo?simulations=500&sample_size=150`, { timeoutMs: 60000 });
  return (
    <Card className="mk-span-7" title="Monte Carlo"
      scope={feed.data ? `${count(feed.data.simulations)} simulations of the next ${feed.data.sample_size || 150} trades, resampled from ${count(feed.data.source_trades)} closed trades` : "Resampled from closed trades"}
      actions={<button type="button" className="mk-btn" onClick={feed.retry} disabled={feed.loading}><RefreshCw className={feed.loading ? "mk-spin" : ""} aria-hidden /> Re-run</button>}>
      <FeedBody feed={feed} rows={6} height={260}>
        {(d) => {
          const fp = d.final_pnl_dist || {};
          const dd = d.max_drawdown_dist || {};
          return (
            <>
              <div className="mk-legend"><span><i className="is-median" /> Median</span><span><i className="is-inner" /> 25–75%</span><span><i className="is-outer" /> 5–95%</span></div>
              {d.curve_bands?.length > 1 ? <FanChart bands={d.curve_bands} /> : <p className="mk-empty">No simulation curve returned.</p>}
              <div className="mk-stat-row is-4">
                <div><span className="mk-label">Profitable runs</span><strong className="mk-num">{pct(fp.profitable_pct)}</strong></div>
                <div><span className="mk-label">Median outcome</span><strong className={`mk-num ${tone(fp.p50)}`}>{money(fp.p50)}</strong></div>
                <div><span className="mk-label">Best case (p95)</span><strong className={`mk-num ${tone(fp.p95)}`}>{money(fp.p95)}</strong></div>
                <div><span className="mk-label">Median max drawdown</span><strong className="mk-num is-down">{money(-Math.abs(dd.p50 || 0))}</strong>
                  <span className="mk-sub">5–95%: {money(-Math.abs(dd.p5 || 0))} to {money(-Math.abs(dd.p95 || 0))}</span></div>
              </div>
            </>
          );
        }}
      </FeedBody>
    </Card>
  );
}

function Streaks() {
  const feed = useFeed(`${API}/analytics/streak-drawdown`, { interval: 30000 });
  return (
    <Card title="Streaks & drawdowns" scope="Per account · drawdown measured on realized equity (starting balance + closed-trade P&L)">
      <FeedBody feed={feed} rows={4}>
        {(d) => (
          <table className="mk-table">
            <thead><tr><th>Account</th><th>Streak</th><th className="r">Current DD</th><th className="r">Max DD</th></tr></thead>
            <tbody>
              {Object.entries(d).filter(([, v]) => v && typeof v === "object").map(([acct, s]) => (
                <tr key={acct}>
                  <td>{acct}</td>
                  <td className={s.streak_type === "win" ? "is-up" : "is-down"}>{s.streak_count} {s.streak_type === "win" ? (s.streak_count === 1 ? "win" : "wins") : (s.streak_count === 1 ? "loss" : "losses")}</td>
                  <td className="r mk-num is-down">{money(-Math.abs(s.current_drawdown))} <span className="mk-sub">{pct(-Math.abs(s.current_drawdown_pct))}</span></td>
                  <td className="r mk-num is-down">{money(-Math.abs(s.max_drawdown))} <span className="mk-sub">{pct(-Math.abs(s.max_drawdown_pct))}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </FeedBody>
    </Card>
  );
}

export default function RiskAccounts({ accounts, trades, heatOk, filter, onFilter, onDetail }) {
  return (
    <div className="mk-risk">
      <p className={`mk-status-line ${heatOk ? "" : "is-warn"}`} role="status">
        {heatOk ? "Portfolio is inside operating limits." : "Risk concentration needs attention: portfolio heat is above 70%."}
        {" "}Pick an account to filter the Live Desk positions and the Trade Ledger.
      </p>
      <AccountCards accounts={accounts} trades={trades} filter={filter} onFilter={onFilter} onDetail={onDetail} />
      <div className="mk-grid-12">
        <MonteCarlo />
        <div className="mk-span-5 mk-stack">
          <PortfolioRisk />
          <Streaks />
        </div>
      </div>
    </div>
  );
}
